import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  filter,
  map,
  takeUntil
} from 'rxjs/operators';
import {
  Observable,
  Subject
} from 'rxjs';
import {
  ActionsSubject,
  select,
  Store
} from '@ngrx/store';

import * as myWatchlistsReducer from '../../../my-watchlists/reducer';
import * as myWatchlistsActions from '../../../my-watchlists/actions/my-watchlists.actions';
import { MyWatchlistsState } from '../../../my-watchlists/reducer';
import { WatchlistWithChildLists } from '../../../watchlists/model/watchlist/watchlist-with-child-lists';
import { WatchlistForm } from '../../../watchlists/model/watchlist/watchlist-form';
import { WatchlistSnapshot } from '../../model/watchlist-snapshot';
import { uniqueName } from '../../../shared/forms/validators/unique-name/unique-name';


/**
 * Dialog to copy a watchlist snapshot
 */
@Component({
  selector: 'p1-copy-watchlist-dialog',
  templateUrl: './copy-watchlist-dialog.component.html',
  styleUrls: ['./copy-watchlist-dialog.component.scss']
})
export class CopyWatchlistDialogComponent implements OnInit, OnDestroy {

  @Input()
  show: boolean = false;

  @Input()
  copyWatchlistInProgress: boolean = false;

  @Input()
  watchlistSnapshot: WatchlistSnapshot;

  @Output()
  closeCopyDialog: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  copyWatchlist: EventEmitter<WatchlistForm> = new EventEmitter<WatchlistForm>();

  watchlistFormGroup: FormGroup;

  watchlists$: Observable<WatchlistWithChildLists[]>;

  availableNames: string[] = [];

  private _unsubscribe: Subject<void> = new Subject();

  constructor(
    private _store: Store<MyWatchlistsState>,
    private _actionsSubject: ActionsSubject,
    private _formBuilder: FormBuilder
  ) {
    this.watchlists$ = this._store.pipe(
      select(myWatchlistsReducer.getFirstLevelWatchlistsWithChildLists),
      map(watchlistArray => (watchlistArray || []).sort((watchlistA, watchlistB) => watchlistB.created > watchlistA.created ? 1 : -1)
      )
    );
  }

  ngOnInit() {
    this._store.dispatch(new myWatchlistsActions.FetchMyWatchlistsAction());

    this._actionsSubject.pipe(
      filter(action => action.type === myWatchlistsActions.FETCH_MY_WATCHLISTS_SUCCEEDED),
      takeUntil(this._unsubscribe)
    ).subscribe(() => {
      // fetch details for list preview
      this.watchlists$.pipe(
        takeUntil(this._unsubscribe),
        filter(watchlists => watchlists && watchlists.length > 0)
      ).subscribe(watchlists => {
        this.availableNames.length = 0;
        watchlists.map(list => {
          this.availableNames.push(list.name);
        });
      });
    });

    this.watchlistFormGroup = this._formBuilder.group({
      name: new FormControl('', [Validators.required])
    }, {
      validator: uniqueName('name', this.availableNames)
    });

    if (this.watchlistSnapshot) {
      this.watchlistFormGroup.patchValue({ name: this.watchlistSnapshot.name });
    }
  }

  /**
   * Unsubscribes from observables
   */
  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  /**
   * Submits the watchlistFormData including the snapshotUuid
   */
  submitWatchlist() {
    if (this.watchlistFormGroup.controls.name.valid) {
      const watchlistFormData: WatchlistForm = {
        name: this.watchlistFormGroup.controls.name.value,
        snapshotUuid: this.watchlistSnapshot.uuid
      };

      this.copyWatchlist.emit(watchlistFormData);
    }
  }

  closeDialog() {
    this.closeCopyDialog.emit();
  }
}
